/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { appConfig, appStrings } from '../../app.config';
import { FilterItem, FilterTypes, IFilterOption } from '@shared/models/filters';
import { Subscription } from 'rxjs';
import { FilterService } from '@shared/services/filters/filter.service';
import { MatAccordion } from '@angular/material/expansion';
import { DateRange, DefaultMatCalendarRangeStrategy, MAT_DATE_RANGE_SELECTION_STRATEGY, MatCalendar } from '@angular/material/datepicker';
import { addDays, addMonths, endOfDay, endOfMonth, format, getMonth, getYear, parseISO, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { Store } from '@ngrx/store';
import { FiltersState } from '@shared/state/reducers/filters/filters.reducer';
import { filterActions } from '@shared/state/actions/filters/filters.actions';
import { distinctUntilChanged } from 'rxjs/operators';
import { ListItemStatuses, ListTypes } from '@shared/models/listing';

@Component({
    selector: 'marketplace-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
    providers: [
        {
            provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
            useClass: DefaultMatCalendarRangeStrategy
        }
    ],
    standalone: false
})
export class FiltersComponent implements OnInit, OnDestroy {
  appStrings = appStrings;
  filtersCounter = 0;
  filters: FilterItem[];
  filterTypes = FilterTypes;
  innerHeight: number;
  innerWidth: number;
  listType: ListTypes;
  listUrl: ListItemStatuses | string;
  selectedDate: Date | null;
  selectedDateRange: DateRange<Date>;
  subFilters$: Subscription;
  subFilterStatus$: Subscription;
  subListType$: Subscription;
  subListUrl$: Subscription;
  subTotalRecords$: Subscription;
  subscriptionList = new Subscription();
  today: Date = new Date();
  totalRecords: number;
  startOfCurrentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
  startOfNextWeek = addDays(this.startOfCurrentWeek, 7);
  startOfNextMonth = addMonths(startOfMonth(new Date()), 1);
  @ViewChild(MatAccordion) filtersAccordion: MatAccordion;
  @ViewChild('dateRangeCalendar') dateRangeCalendar: MatCalendar<Date>;

  constructor(
    private _filterService: FilterService,
    private _filtersStore: Store<FiltersState>
  ) {
    this.onResize();
  }

  identifier = (index: number, item: any) => item.name;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.innerWidth = window.innerWidth;
    this.innerHeight = window.innerHeight;
  }

  ngOnInit() {
    this.getTotalRecords();
    this.getFiltersPanelStatus();
    this.getPublishedFilters();
    this.getListType();
    this.getListUrl();
  }

  getListType() {
    this.subListType$ = this._filterService.listType$.subscribe({
      next: response => {
        if (response) {
          this.listType = response;
        }
        this.subscriptionList.add(this.subListType$);
      },
      error: error => {
        console.error(error);
      }
    });
  }

  getListUrl() {
    this.subListUrl$ = this._filterService.listUrl$.subscribe({
      next: response => {
        if (response) {
          this.listUrl = response;
        }
        this.subscriptionList.add(this.subListUrl$);
      },
      error: error => {
        console.error(error);
      }
    });
  }

  getPublishedFilters() {
    this.subFilters$ = this._filterService.filters$
      .pipe(
        distinctUntilChanged((prev, current) => {
          return prev === current;
        })
      )
      .subscribe({
        next: response => {
          if (response) {
            this.filters = response;
          }
          this.subscriptionList.add(this.subFilters$);
        }
      });
  }

  /**
   * Filter status (OPEN/CLOSE)
   */
  getFiltersPanelStatus() {
    this.subFilterStatus$ = this._filterService.filtersActive$
      .pipe(
        distinctUntilChanged((prev, current) => {
          return prev === current;
        })
      )
      .subscribe({
        next: response => {
          if (response) {
            this.setOptionsSelected(this.filters);
            this.setFiltersCounter(this.filters);
          } else {
            if (this.filtersAccordion) {
              this.filtersAccordion.closeAll();
            }
          }
          this.subscriptionList.add(this.subFilterStatus$);
        }
      });
  }

  /**
   * Set the selected options number and filters applied message
   * @param filters
   */
  setOptionsSelected(filters: FilterItem[]) {
    const filtersLocal: FilterItem[] = filters.map(filter => JSON.parse(JSON.stringify(filter)));

    filtersLocal.forEach(filter => {
      if (filter.type === FilterTypes.SELECT_SINGLE || filter.type === FilterTypes.SELECT_MULTIPLE) {
        filter.options_selected = 0;
        filter.options.forEach(option => {
          if (option.selected && option.value !== 'all') {
            filter.options_selected += 1;
          }
        });
      }
      if (filter.id === 'filter_rating' && this.listUrl === ListItemStatuses.UPCOMING) {
        filter.options_selected = 0;
      }
      this.setFiltersMessage(filter);
    });
    this.setFiltersCounter(filtersLocal);

    if (this.listType === ListTypes.REQUESTS) {
      this.dispatchRequestFilters(filtersLocal);
    } else if (this.listType === ListTypes.QUOTES) {
      this.dispatchQuoteFilters(filtersLocal);
    } else if (this.listType === ListTypes.ENQUIRIES) {
      this.dispatchEnquiryFilters(filtersLocal);
    } else if (this.listType === ListTypes.CUSTOMERS) {
      this.dispatchCustomerFilters(filtersLocal);
    } else {
      this.dispatchBookingFilters(filtersLocal);
    }

    this.filters = filtersLocal.map(filter => ({ ...filter }));
  }

  /**
   * Set the filter applied message based on selection made or after reset
   * @param filter
   */
  setFiltersMessage(filter: FilterItem) {
    switch (filter.type) {
      case FilterTypes.SELECT_SINGLE:
        if (filter.options_selected === 0) {
          filter.filters_applied_message = 'Any';
        } else {
          filter.options.forEach(option => {
            if (option.selected) {
              filter.filters_applied_message = option.name;
            }
          });
        }
        break;
      case FilterTypes.SELECT_MULTIPLE:
        if (filter.options_selected === 0) {
          filter.filters_applied_message = 'All';
        } else {
          filter.filters_applied_message = `${filter.options_selected} applied`;
        }
        break;
      case FilterTypes.DATE_PICKER:
        if (!filter.value) {
          filter.filters_applied_message = 'Any';
        } else {
          filter.filters_applied_message = `${format(parseISO(filter.value.startDate), appConfig.date_short)}`;
        }
        break;
      case FilterTypes.DATERANGE_PICKER:
        if (!filter.value || !filter.value.startDate || !filter.value.endDate) {
          filter.filters_applied_message = 'Any';
          filter.options_selected = 0;
        } else if (filter.value && filter.value.startDate && filter.value.endDate) {
          if (format(parseISO(filter.value.startDate), appConfig.date_short) === format(parseISO(filter.value.endDate), appConfig.date_short)) {
            filter.filters_applied_message = `${format(parseISO(filter.value.startDate), appConfig.date_short)}`;
          } else {
            filter.filters_applied_message = `${format(parseISO(filter.value.startDate), appConfig.date_short)} - ${format(
              parseISO(filter.value.endDate),
              appConfig.date_short
            )}`;
          }
        }
        break;
      default:
        break;
    }
  }

  /**
   * Set or update Filters Store
   * @param requestFilters
   */
  dispatchRequestFilters(requestFilters: FilterItem[]) {
    this._filtersStore.dispatch(
      filterActions.setRequestFilters({
        request_filters: requestFilters
      })
    );
  }

  /**
   * Set or update Filters Store
   * @param quoteFilters
   */
  dispatchQuoteFilters(quoteFilters: FilterItem[]) {
    this._filtersStore.dispatch(
      filterActions.setQuoteFilters({
        quote_filters: quoteFilters
      })
    );
  }

  /**
   * Set or update Filters Store
   * @param bookingFilters
   */
  dispatchBookingFilters(bookingFilters: FilterItem[]) {
    this._filtersStore.dispatch(
      filterActions.setBookingFilters({
        booking_filters: bookingFilters
      })
    );
  }

  /**
   * Set or update Filters Store
   * @param enquiryFilters
   */
  dispatchEnquiryFilters(enquiryFilters: FilterItem[]) {
    this._filtersStore.dispatch(
      filterActions.setEnquiryFilters({
        enquiry_filters: enquiryFilters
      })
    );
  }

  dispatchCustomerFilters(customerFilters: FilterItem[]) {
    this._filtersStore.dispatch(
      filterActions.setCustomerFilters({
        customer_filters: customerFilters
      })
    );
  }

  /**
   * Filters counter present in the footer
   * @param filters
   */
  setFiltersCounter(filters: FilterItem[]) {
    this.filtersCounter = 0;
    filters.forEach(filter => {
      if (filter.options_selected > 0) {
        this.filtersCounter += 1;
      }
    });
  }

  /**
   * Get total records from a list component
   */
  getTotalRecords() {
    this.subTotalRecords$ = this._filterService.totalRecords.subscribe({
      next: response => {
        this.totalRecords = response;
        this.subscriptionList.add(this.subTotalRecords$);
      },
      error: error => {
        console.error(error);
      }
    });
  }

  /**
   * Single & multiple Select filter
   * @param filter
   * @param options
   * @param selectedOption
   */
  optionsChanged(filter: FilterItem, options: IFilterOption[], selectedOption: IFilterOption) {
    const option = { ...selectedOption };

    const localOptions: IFilterOption[] = options.map(item => JSON.parse(JSON.stringify(item)));

    const filtersLocal: FilterItem[] = this.filters.map(filter => JSON.parse(JSON.stringify(filter)));

    option.selected = !option.selected;

    if (option.value === 'all') {
      localOptions.forEach(el => {
        el.selected = el.value === option.value;
      });
    } else {
      localOptions.forEach(el => {
        if (el.value === option.value) {
          el.selected = option.selected;
        } else if (
          filter?.type &&
          ((filter.type === FilterTypes.SELECT_MULTIPLE && el.value === 'all') || filter.type === FilterTypes.SELECT_SINGLE)
        ) {
          el.selected = false;
        }
      });
    }

    const allDeselected = localOptions.every(obj => obj.selected === false);

    if (allDeselected) {
      localOptions.forEach(el => {
        el.selected = el.value === 'all';
      });
    }

    if (filtersLocal.find(el => el.id === filter?.id)?.options) {
      filtersLocal.find(el => el.id === filter?.id).options = localOptions;
    }
    this.filters = filtersLocal;

    this.setOptionsSelected(filtersLocal);
  }

  /**
   * Single date selection
   * @param event
   */
  dateChanged(event) {
    this.selectedDate = event;
    const datePickerFilter = this.filters.find(el => el.type === FilterTypes.DATE_PICKER);
    if (datePickerFilter) {
      datePickerFilter.options_selected = 1;
      datePickerFilter.value = {
        startDate: this.selectedDate
      };
    }
    this.setOptionsSelected(this.filters);
    this.setFiltersCounter(this.filters);
  }

  /**
   * Date range selection
   * @param date
   */
  dateRangeChanged(date: Date): void {
    const filtersLocal: FilterItem[] = this.filters.map(filter => JSON.parse(JSON.stringify(filter)));

    if (!this.selectedDateRange || !this.selectedDateRange.start || isNaN(this.selectedDateRange.start.getTime())) {
      this.selectedDateRange = new DateRange(startOfDay(date), endOfDay(date));
    } else if (date > this.selectedDateRange.start) {
      if (
        this.selectedDateRange.start &&
        this.selectedDateRange.end &&
        (format(this.selectedDateRange.start, appConfig.date_short) === format(date, appConfig.date_short) ||
          format(this.selectedDateRange.end, appConfig.date_short) === format(date, appConfig.date_short))
      ) {
        this.selectedDateRange = new DateRange(startOfDay(date), endOfDay(date));
      } else {
        this.selectedDateRange = new DateRange(startOfDay(this.selectedDateRange.start), endOfDay(date));
      }
    } else {
      this.selectedDateRange = new DateRange(startOfDay(date), endOfDay(date));
    }
    filtersLocal.find(el => el.type === FilterTypes.DATERANGE_PICKER).options_selected = 1;
    filtersLocal.find(el => el.type === FilterTypes.DATERANGE_PICKER).value = {
      startDate: this.selectedDateRange?.start,
      endDate: this.selectedDateRange?.end
    };

    filtersLocal
      .find(filter => filter.type === FilterTypes.DATERANGE_PICKER)
      .options.forEach(option => {
        option.selected = false;
      });

    this.filters = filtersLocal;

    this.setOptionsSelected(filtersLocal);
    this.setFiltersCounter(filtersLocal);
  }

  /**
   * Set pre-defined date range from options provided
   * @param filter
   * @param selectedOption
   */
  setPreDefinedDateRange(filter: FilterItem, selectedOption: IFilterOption) {
    const filterLocal = JSON.parse(JSON.stringify(filter));
    const option = JSON.parse(JSON.stringify(selectedOption));
    const filtersLocal: FilterItem[] = this.filters.map(filter => JSON.parse(JSON.stringify(filter)));

    filterLocal.options.forEach(el => {
      el.selected = el.value === option.value;
    });

    switch (option.value) {
      case 'today':
        this.selectedDateRange = new DateRange(startOfDay(this.today), endOfDay(this.today));
        break;
      case 'tomorrow':
        this.selectedDateRange = new DateRange(startOfDay(addDays(this.today, 1)), endOfDay(addDays(this.today, 1)));
        break;
      case 'next_week':
        this.selectedDateRange = new DateRange(this.startOfNextWeek, addDays(this.startOfNextWeek, 6));
        break;
      case 'next_month':
        this.selectedDateRange = new DateRange(this.startOfNextMonth, endOfMonth(this.startOfNextMonth));
        break;
      default:
        this.selectedDateRange = null;
        break;
    }

    if (this.selectedDateRange && this.selectedDateRange.start) {
      this.dateRangeCalendar.activeDate = new Date(getYear(this.selectedDateRange.start), getMonth(this.selectedDateRange.start));
    }
    this.dateRangeCalendar.updateTodaysDate();
    filtersLocal.find(el => el.type === FilterTypes.DATERANGE_PICKER).options_selected = 1;
    filtersLocal.find(el => el.type === FilterTypes.DATERANGE_PICKER).value = {
      startDate: this.selectedDateRange && this.selectedDateRange?.start ? this.selectedDateRange.start : null,
      endDate: this.selectedDateRange && this.selectedDateRange?.end ? this.selectedDateRange.end : null
    };
    filtersLocal
      .find(el => el.type === FilterTypes.DATERANGE_PICKER)
      .options.forEach(item => {
        item.selected = item.value === option.value;
      });
    this.setOptionsSelected(filtersLocal);
    this.setFiltersCounter(filtersLocal);
  }

  resetFilters(filters: FilterItem[]) {
    const filtersLocal: FilterItem[] = filters.map(filter => JSON.parse(JSON.stringify(filter)));

    filtersLocal.forEach(filter => {
      filter.options_selected = 0;
      if (filter.options && filter.options.length) {
        filter.options.forEach(option => {
          option.selected = option.value === 'all';
        });
      }

      filter.value = null;
    });
    this.selectedDate = null;
    this.selectedDateRange = null;
    if (this.filtersAccordion) {
      this.filtersAccordion.closeAll();
    }
    this.setOptionsSelected(filtersLocal);
  }

  viewResults() {
    this._filterService.toggleFilters(false);
  }

  ngOnDestroy() {
    if (this.filtersAccordion) {
      this.filtersAccordion.closeAll();
    }
    this.subscriptionList.unsubscribe();
  }
}
